import { Head, Link } from '@inertiajs/react';
import { ArrowUpRight } from 'lucide-react';
import type { VoteIndexShow } from '@/types';

type Edition = VoteIndexShow['editions'][number];

// Where each edition was held, so a date at 00:00Z doesn't slip a day.
const TIME_ZONES: Record<string, string> = {
  '25': 'America/Santiago',
  '26-ar': 'America/Argentina/Buenos_Aires',
  '26-mx': 'America/Mexico_City',
  '26-co': 'America/Bogota',
};

function formatDates(edition: Edition): string | null {
  if (!edition.startsAt) return null;
  const timeZone = TIME_ZONES[edition.slug] ?? 'UTC';
  const start = new Date(edition.startsAt);
  const end = edition.endsAt ? new Date(edition.endsAt) : start;
  const day = (d: Date) =>
    new Intl.DateTimeFormat('es', { day: 'numeric', timeZone }).format(d);
  const month = (d: Date) =>
    new Intl.DateTimeFormat('es', { month: 'long', timeZone }).format(d);
  const year = new Intl.DateTimeFormat('es', { year: 'numeric', timeZone }).format(end);

  if (day(start) === day(end) && month(start) === month(end)) {
    return `${day(start)} de ${month(start)} ${year}`;
  }
  if (month(start) === month(end)) {
    return `${day(start)}–${day(end)} de ${month(end)} ${year}`;
  }
  return `${day(start)} de ${month(start)} – ${day(end)} de ${month(end)} ${year}`;
}

// A superellipse ("squircle") in bounding-box units; CSS border-radius can't draw one.
function SquircleDefs() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true">
      <defs>
        <clipPath id="squircle" clipPathUnits="objectBoundingBox">
          <path d="M0.5,0 C0.88,0 1,0.12 1,0.5 C1,0.88 0.88,1 0.5,1 C0.12,1 0,0.88 0,0.5 C0,0.12 0.12,0 0.5,0 Z" />
        </clipPath>
      </defs>
    </svg>
  );
}

// Track names lead with their emoji ("🌐 Simulations"); that emoji is the badge.
function trackIcon(name: string | null): string | null {
  const match = name?.match(/^\p{Extended_Pictographic}\uFE0F?/u);
  return match ? match[0] : null;
}

function LogoCarousel({ edition }: { edition: Edition }) {
  if (edition.logos.length === 0) return null;
  // Short lists would leave a gap in the loop, so repeat until a half is wide enough.
  const repeats = Math.max(1, Math.ceil(16 / edition.logos.length));
  const half = Array.from({ length: repeats }, () => edition.logos).flat();
  const duration = Math.max(30, half.length * 2.5);

  return (
    <div
      className="group relative overflow-hidden py-2"
      style={{
        maskImage:
          'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
      }}
    >
      <div
        className="flex w-max gap-4 group-hover:[animation-play-state:paused]"
        // Longhands, not the `animation` shorthand: it would reset play-state inline and
        // beat the hover class above.
        style={{
          animationName: 'vote-index-scroll',
          animationDuration: `${duration}s`,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
        }}
      >
        {[0, 1].map((copy) =>
          half.map((logo, i) => (
            <a
              // biome-ignore lint/suspicious/noArrayIndexKey: the list is repeated on purpose
              key={`${copy}-${i}`}
              href={logo.path}
              target="_blank"
              rel="noreferrer"
              title={logo.trackName ? `${logo.name} · ${logo.trackName}` : logo.name}
              aria-hidden={copy === 1}
              tabIndex={copy === 1 ? -1 : undefined}
              className="relative block size-20 shrink-0 transition-transform duration-200 hover:scale-110 sm:size-24"
            >
              <span
                className="block size-full bg-white/10"
                style={{ clipPath: 'url(#squircle)' }}
              >
                <img
                  src={logo.logoUrl}
                  alt={logo.name}
                  loading="lazy"
                  className="size-full object-cover"
                />
              </span>
              {trackIcon(logo.trackName) && (
                <span className="absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full border border-white/20 bg-black text-xs leading-none">
                  {trackIcon(logo.trackName)}
                </span>
              )}
            </a>
          )),
        )}
      </div>
    </div>
  );
}

function EditionCard({ edition }: { edition: Edition }) {
  const dates = formatDates(edition);

  return (
    <section className="min-w-0 rounded-3xl border border-white/15 bg-white/[0.03] p-5 sm:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="font-logo text-2xl tracking-tight sm:text-4xl">
            {edition.name}
          </h2>
          <p className="text-sm text-white/60">
            {[edition.city, dates].filter(Boolean).join(' · ')}
            {edition.projectCount > 0 && ` · ${edition.projectCount} proyectos`}
          </p>
        </div>
        <Link
          href={edition.votePath}
          className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-white px-5 py-2.5 font-medium text-black text-sm transition-colors hover:bg-white/80"
        >
          Ver proyectos
          <ArrowUpRight className="size-4" />
        </Link>
      </div>

      {edition.tracks.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {edition.tracks.map((track) => (
            <li
              key={track}
              className="rounded-full border border-white/20 px-3 py-1 text-white/80 text-xs"
            >
              {track}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6">
        <LogoCarousel edition={edition} />
      </div>
    </section>
  );
}

export default function Show({ editions, title, description, ogImageUrl }: VoteIndexShow) {
  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImageUrl} />
      </Head>
      <style>{`
        @keyframes vote-index-scroll {
          to { transform: translateX(calc(-50% - 0.5rem)); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="vote-index-scroll"] { animation: none !important; }
        }
      `}</style>
      <SquircleDefs />
      <main className="min-h-screen overflow-x-clip bg-black text-white">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-12 sm:px-6 sm:py-16">
          <header className="flex flex-col items-center gap-3 text-center">
            <h1 className="font-logo text-3xl lowercase tracking-tighter sm:text-6xl">
              platanus hack [vote]
            </h1>
          </header>
          {editions.map((edition) => (
            <EditionCard key={edition.slug} edition={edition} />
          ))}
        </div>
      </main>
    </>
  );
}
